import { faArrowUp, faInputText } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { VariableDefinitionModel } from '@supply-mover-2-0/shared';
import { useCallback, useEffect, useState } from 'react';
import { SelectSearch } from '../../components/forms/SelectSearch';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import type { AgentInputVariable } from '../services/agentService';
import { AgentService } from '../services/agentService';

interface AgentStepConfigProps {
	config: any;
	setConfig: (config: any) => void;
	stepType: 'agent' | 'agent_call';
	onOpenVariableMapping?: () => void;
}

export function AgentStepConfig({
	config,
	setConfig,
	stepType,
	onOpenVariableMapping,
}: AgentStepConfigProps) {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [selectedAgent, setSelectedAgent] = useState<any>(null);

	// Load agent details when agentId exists
	useEffect(() => {
		if (config.agentId && !selectedAgent) {
			loadAgentDetails(String(config.agentId));
		}
	}, [config.agentId]);

	// Load recent agents
	const loadRecentAgents = useCallback(async () => {
		try {
			const result = await AgentService.getAgents({ page: 1, pageSize: 10 });

			if (result.success && result.data) {
				// Filter for active agents
				const activeAgents = result.data
					.filter(
						(agent: any) => agent.isActive === true || agent.isActive === 1,
					)
					.map((agent: any) => ({
						value: agent.id.toString(),
						label: agent.name,
						subtitle: AgentService.getAgentTypeLabel(agent.agent_type || ''),
					}));

				return activeAgents;
			}

			return [];
		} catch (error) {
			console.error('Failed to load recent agents:', error);

			return [];
		}
	}, []);

	// Search agents
	const searchAgents = useCallback(async (query: string) => {
		try {
			const result = await AgentService.getAgents({
				page: 1,
				pageSize: 20,
				search: query,
			});

			if (result.success && result.data) {
				// Filter for active agents
				const filteredAgents = result.data
					.filter(
						(agent: any) => agent.isActive === true || agent.isActive === 1,
					)
					.map((agent: any) => ({
						value: agent.id.toString(),
						label: agent.name,
						subtitle: AgentService.getAgentTypeLabel(agent.agent_type || ''),
					}));

				return filteredAgents;
			}

			return [];
		} catch (error) {
			console.error('Failed to search agents:', error);
			return [];
		}
	}, []);

	// Load agent details
	const loadAgentDetails = async (agentId: string) => {
		try {
			const result = await AgentService.getAgent(parseInt(agentId));
			if (result.success && result.data) {
				setSelectedAgent(result.data);
			}
		} catch (error) {
			console.error('Failed to load agent details:', error);
		}
	};

	// Load agent by ID for SelectSearch display
	const loadAgentById = useCallback(async (agentId: string) => {
		try {
			const result = await AgentService.getAgent(parseInt(agentId));
			if (result.success && result.data) {
				return {
					value: result.data.id.toString(),
					label: result.data.name,
					subtitle: AgentService.getAgentTypeLabel(
						result.data.agent_type || '',
					),
				};
			}
			return null;
		} catch (error) {
			console.error('Failed to load agent by ID:', error);
			return null;
		}
	}, []);

	// Handle agent selection
	const handleAgentSelect = (agentId: string) => {
		if (agentId) {
			loadAgentDetails(agentId);
			const agentIdNum = parseInt(agentId);

			// Also update the config with agent name for display purposes
			loadRecentAgents().then((agents) => {
				const selected = agents.find((a: any) => a.value === agentId);
				if (selected) {
					setConfig({
						...config,
						agentId: agentIdNum,
						agentName: selected.label,
					});
				} else {
					setConfig({
						...config,
						agentId: agentIdNum,
					});
				}
			});
		} else {
			setSelectedAgent(null);
			setConfig({
				...config,
				agentId: undefined,
				agentName: undefined,
			});
		}
	};

	// Get title based on step type
	const getTitle = () => {
		if (stepType === 'agent_call') {
			return t('Agent Call Configuration');
		}
		return t('AI Agent Configuration');
	};

	const getSelectLabel = () => {
		if (stepType === 'agent_call') {
			return t('Select AI Agent to Call');
		}
		return t('Select AI Agent');
	};

	return (
		<div className="space-y-6">
			<div>
				<h3
					className={`text-base font-semibold mb-4 ${colors.classes.textPrimary}`}
				>
					{getTitle()}
				</h3>

				{/* Agent Selection with Dynamic Search */}
				<div>
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{getSelectLabel()}
					</label>
					<SelectSearch
						mode="dynamic"
						value={config.agentId?.toString()}
						onChange={handleAgentSelect}
						placeholder={t('Search agents...')}
						loadRecent={loadRecentAgents}
						searchItems={searchAgents}
						loadItemById={loadAgentById}
						recentGroupLabel={t('Recent Agents')}
						searchGroupLabel={t('Search Results')}
						emptyRecentMessage={t('No recent agents')}
						emptySearchMessage={t('No agents found')}
						searchDebounceMs={300}
						minSearchLength={1}
						fullWidth
					/>
				</div>

				{/* Agent Details */}
				{selectedAgent && (
					<div className={`mt-4 p-4 rounded-lg ${colors.classes.bgSecondary}`}>
						<div className="space-y-3">
							<div>
								<h4
									className={`text-sm font-medium ${colors.classes.textPrimary}`}
								>
									{t('Agent Type')}
								</h4>
								<p className={`text-sm mt-1 ${colors.classes.textSecondary}`}>
									{AgentService.getAgentTypeLabel(
										selectedAgent.agent_type || '',
									)}
								</p>
							</div>

							{selectedAgent.description && (
								<div>
									<h4
										className={`text-sm font-medium ${colors.classes.textPrimary}`}
									>
										{t('Description')}
									</h4>
									<p className={`text-sm mt-1 ${colors.classes.textSecondary}`}>
										{selectedAgent.description}
									</p>
								</div>
							)}

							{selectedAgent.system_prompt && (
								<div>
									<h4
										className={`text-sm font-medium ${colors.classes.textPrimary}`}
									>
										{t('System Prompt')}
									</h4>
									<p
										className={`text-sm mt-1 ${colors.classes.textSecondary} font-mono`}
									>
										{selectedAgent.system_prompt.length > 200
											? `${selectedAgent.system_prompt.substring(0, 200)}...`
											: selectedAgent.system_prompt}
									</p>
								</div>
							)}

							{/* Input Variables Info */}
							{selectedAgent.metadata?.inputVariables != null &&
								selectedAgent.metadata.inputVariables.length > 0 && (
									<div className="mt-4 pt-4 border-t">
										<div className="flex items-center justify-between mb-2">
											<div className="flex items-center gap-2">
												<FontAwesomeIcon
													icon={faInputText}
													className={colors.classes.textSecondary}
													size="sm"
												/>
												<h4
													className={`text-sm font-medium ${colors.classes.textPrimary}`}
												>
													{t('Input Variables')}
												</h4>
											</div>
											{stepType === 'agent_call' &&
												onOpenVariableMapping != null && (
													<button
														type="button"
														onClick={onOpenVariableMapping}
														className={`text-xs font-medium px-2 py-1 rounded ${colors.classes.textPrimary} hover:${colors.classes.bgTertiary} transition-colors`}
													>
														{t('Map Variables')} (
														{selectedAgent.metadata.inputVariables.length})
													</button>
												)}
										</div>
										<div className="space-y-2">
											{selectedAgent.metadata.inputVariables.map(
												(variable: AgentInputVariable, index: number) => (
													<div
														key={index}
														className={`text-xs p-2 rounded ${colors.classes.bgPrimary}`}
													>
														<div className="flex items-center gap-2">
															<span
																className={`font-medium ${colors.classes.textPrimary}`}
															>
																{variable.label || variable.name}
															</span>
															{variable.required === true && (
																<span
																	className={`text-xs px-1.5 py-0.5 rounded ${colors.classes.bgError} ${colors.classes.textError}`}
																>
																	{t('Required')}
																</span>
															)}
															<span className={colors.classes.textSecondary}>
																({variable.type})
															</span>
														</div>
													</div>
												),
											)}
										</div>
										{stepType === 'agent_call' && (
											<p
												className={`text-xs mt-2 ${colors.classes.textSecondary}`}
											>
												{t(
													'Click "Map Variables" to provide values for these input variables from your automation flow data.',
												)}
											</p>
										)}
									</div>
								)}

							{/*
							 * Output variables — what this agent is expected to collect
							 * during the call. After the call ends, the post-call extraction
							 * service fills these into the step's collectedVariables map
							 * so downstream flow steps can read them.
							 */}
							{selectedAgent.metadata?.outputVariables != null &&
								selectedAgent.metadata.outputVariables.length > 0 && (
									<div className="mt-4 pt-4 border-t">
										<div className="flex items-center gap-2 mb-2">
											<FontAwesomeIcon
												icon={faArrowUp}
												className={colors.classes.textSecondary}
												size="sm"
											/>
											<h4
												className={`text-sm font-medium ${colors.classes.textPrimary}`}
											>
												{t('Output Variables')}
											</h4>
										</div>
										<div className="space-y-2">
											{(
												selectedAgent.metadata
													.outputVariables as Array<VariableDefinitionModel>
											).map((variable, index) => (
												<div
													key={index}
													className={`text-xs p-2 rounded ${colors.classes.bgPrimary}`}
												>
													<div className="flex items-center gap-2">
														<span
															className={`font-medium ${colors.classes.textPrimary}`}
														>
															{variable.label != null && variable.label !== ''
																? variable.label
																: variable.name}
														</span>
														{variable.required === true && (
															<span
																className={`text-xs px-1.5 py-0.5 rounded ${colors.classes.bgError} ${colors.classes.textError}`}
															>
																{t('Required')}
															</span>
														)}
														<span className={colors.classes.textSecondary}>
															({variable.type})
														</span>
													</div>
													{variable.description != null &&
														variable.description !== '' && (
															<p
																className={`text-xs mt-1 ${colors.classes.textSecondary}`}
															>
																{variable.description}
															</p>
														)}
												</div>
											))}
										</div>
										{stepType === 'agent_call' && (
											<p
												className={`text-xs mt-2 ${colors.classes.textSecondary}`}
											>
												{t(
													'These values will be available to downstream steps as',
												)}{' '}
												<code className="font-mono">
													{'{{step.collectedVariables.<name>}}'}
												</code>
												.
											</p>
										)}
									</div>
								)}
						</div>
					</div>
				)}
			</div>

			{stepType === 'agent_call' && (
				<div
					className={`p-3 rounded ${colors.classes.bgInfo} ${colors.classes.bgInfoLight}`}
				>
					<p className={`text-sm ${colors.classes.textPrimary}`}>
						<strong>{t('Note:')}</strong>{' '}
						{t(
							'Agent Call steps allow you to invoke AI agents within your automation flow to process data, make decisions, or generate content based on the current execution context.',
						)}
					</p>
				</div>
			)}
		</div>
	);
}
